import { Injectable, OnDestroy, Signal, computed, inject, isDevMode, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Subscription } from 'rxjs';

export type SkeletonArchetype = 'table' | 'cards' | 'dashboard' | 'form' | 'detail';

/** The archetype a route falls back to when it declares none. `table` because it is
 *  the majority shape in this app, so an un-annotated route degrades to the most
 *  likely layout rather than to nothing. */
export const DEFAULT_ARCHETYPE: SkeletonArchetype = 'table';

export const SKELETON_ARCHETYPES: readonly SkeletonArchetype[] = [
  'table',
  'cards',
  'dashboard',
  'form',
  'detail',
];

/** Nothing renders for this long after a navigation starts. A warm-cached lazy
 *  chunk resolves faster than this, and a 40ms flash of grey looks more broken than
 *  a brief pause on an already-painted shell. */
export const SKELETON_SHOW_DELAY_MS = 120;

/** Once shown, the skeleton stays at least this long. Without it, a chunk landing at
 *  130ms blinks the skeleton in and straight back out. */
export const SKELETON_MIN_VISIBLE_MS = 300;

/**
 * Tracks whether a router navigation is in flight for long enough to be worth
 * showing a skeleton for, and which archetype that skeleton should use.
 *
 * The two timing guards are the difference between this reading as polish and
 * reading as flicker, and they are the reason this is a service rather than three
 * lines in MainLayoutComponent: here they are testable with fake timers.
 */
@Injectable({ providedIn: 'root' })
export class NavigationBusyService implements OnDestroy {
  private readonly router = inject(Router);

  private readonly visible = signal(false);
  private readonly pendingArchetype = signal<SkeletonArchetype>(DEFAULT_ARCHETYPE);
  private readonly pendingListPanel = signal(false);

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;
  private subscription: Subscription;

  /** The id of the navigation currently being tracked. `NavigationEnd` /
   *  `NavigationCancel` / `NavigationError` all carry an id, but a stale event for a
   *  superseded navigation can arrive after the next one has already started —
   *  Angular emits `NavigationCancel(id: 1)` *after* `NavigationStart(id: 2)` when
   *  navigation 2 supersedes navigation 1. Settle events are only acted on when
   *  their id matches this one, so a stale cancel can't kill the live navigation's
   *  timer. */
  private pendingId: number | null = null;

  readonly busy: Signal<boolean> = computed(() => this.visible());
  readonly archetype: Signal<SkeletonArchetype> = computed(() => this.pendingArchetype());
  readonly listPanel: Signal<boolean> = computed(() => this.pendingListPanel());

  constructor() {
    this.subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.onStart(event.id, event.url);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.onSettled(event.id);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearTimers();
  }

  private onStart(id: number, url: string): void {
    this.pendingId = id;
    this.clearTimers();

    // Resolved once here, from the event's own URL, and handed to both lookups —
    // see currentRoutePath() for why this can no longer come from router state.
    const path = this.currentRoutePath(url);
    this.pendingArchetype.set(this.resolveArchetype(path));
    this.pendingListPanel.set(this.resolveListPanel(path));

    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.shownAt = this.now();
      this.visible.set(true);
    }, SKELETON_SHOW_DELAY_MS);
  }

  private onSettled(id: number): void {
    // A stale settle event for a navigation that has already been superseded —
    // ignore it so it can't tear down the live navigation's timer.
    if (id !== this.pendingId) {
      return;
    }

    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;

      if (!this.visible()) {
        // Never showed. Cancel and leave the page alone — this is the warm-chunk path.
        return;
      }
      // Already visible from an earlier show (a prior navigation's skeleton is
      // still up and this one superseded it before its own show-timer fired).
      // Fall through to the remaining-time logic below, anchored on the earlier
      // shownAt, instead of leaving `visible` stuck true with no timer running.
    }

    if (!this.visible()) {
      return;
    }

    const remaining = SKELETON_MIN_VISIBLE_MS - (this.now() - this.shownAt);

    if (remaining <= 0) {
      this.visible.set(false);
      return;
    }

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.visible.set(false);
    }, remaining);
  }

  /** Reads `data.skeleton` off the route being navigated to. Falls back rather than
   *  throwing: a route with a typo should render the default shape, and
   *  app.routes.spec.ts is what turns the typo into a failing build. */
  private resolveArchetype(path: string): SkeletonArchetype {
    // Match the flattened path against the route config. Router state for the
    // target route does not exist yet during NavigationStart, so this reads the
    // static config rather than the (not-yet-built) ActivatedRouteSnapshot tree.
    const candidate = findRouteData(this.router.config, path);

    // A miss here degrades safely to DEFAULT_ARCHETYPE, but silently — the last
    // two ways this fired for real (finalUrl read too early, matrix params left
    // in the path) both shipped invisibly until someone noticed the wrong
    // skeleton on screen. Surface misses in dev so a URL shape currentRoutePath()
    // doesn't anticipate gets caught in development rather than by inspection.
    // Expected to fire for the one accepted caveat (the '**' redirect, looked up
    // under its pre-redirect path) — that's fine, it's a known no-op in dev only.
    if (candidate === undefined && isDevMode()) {
      console.warn(
        `[NavigationBusyService] no route data found for path "${path}" — falling back to the default skeleton ('${DEFAULT_ARCHETYPE}', no list panel).`,
      );
    }

    return SKELETON_ARCHETYPES.includes(candidate as SkeletonArchetype)
      ? (candidate as SkeletonArchetype)
      : DEFAULT_ARCHETYPE;
  }

  /** Whether the incoming route renders a master-detail list panel. Defaults to
   *  false: an un-annotated route gets a full-width skeleton — the behaviour
   *  before this existed — rather than a phantom column that vanishes on arrival.
   *  app.routes.spec.ts is what makes an omission fail the build. */
  private resolveListPanel(path: string): boolean {
    return findRouteData(this.router.config, path, '', 'listPanel') === true;
  }

  /** The incoming navigation's path, stripped of its leading slash and of
   *  everything a route config path never includes: the query string (`?`),
   *  matrix parameters (`;`, e.g. `/marketing;uniqs=a,b,c` from
   *  super-action.component.ts's `router.navigate(['/marketing', { uniqs }])`),
   *  and a fragment (`#`). Any one of those left in would make the path miss in
   *  `findRouteData` and silently fall back to the defaults.
   *
   *  Deliberately built from the `NavigationStart` event's own `url` rather than
   *  `router.getCurrentNavigation()`: that transition's `finalUrl` is not written
   *  by the router until *after* `NavigationStart` is emitted (redirects/guards
   *  resolve later in the pipeline), so reading it here always saw `undefined` and
   *  every archetype lookup silently fell back to the defaults. `event.url` is the
   *  serialized `extractedUrl`, already available synchronously when
   *  `NavigationStart` fires.
   *
   *  Caveat: because `extractedUrl` is pre-redirect, a URL that only resolves via
   *  a `redirectTo` (e.g. the `{ path: '**', redirectTo: '' }` catch-all) is looked
   *  up under its original, unmatched path and falls back to the defaults. That is
   *  the only redirect in this route config, so it is accepted rather than fixed. */
  private currentRoutePath(url: string): string {
    return url.split(/[?;#]/)[0].replace(/^\//, '');
  }

  private now(): number {
    return Date.now();
  }

  private clearTimers(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}

/** Compares a route config path against a real, resolved path segment by segment.
 *  A route segment beginning with `:` (a param, e.g. `:uniq`) matches any single
 *  non-empty segment; every other segment must match literally, and both paths
 *  must have the same number of segments. `admin/clients/:uniq` is the only
 *  parameterised route in this app's config — plain string equality would never
 *  match it against a real URL like `admin/clients/abc123`. */
function pathMatchesRoute(routeFull: string, path: string): boolean {
  const routeSegments = routeFull.split('/').filter(Boolean);
  const pathSegments = path.split('/').filter(Boolean);

  if (routeSegments.length !== pathSegments.length) {
    return false;
  }

  return routeSegments.every(
    (segment, i) => segment.startsWith(':') || segment === pathSegments[i],
  );
}

/** Walks a route config looking for the entry whose accumulated path matches `path`,
 *  returning its `data.skeleton`. Exported for testing. */
export function findRouteData(
  routes: readonly { path?: string; data?: Record<string, unknown>; children?: unknown[] }[],
  path: string,
  prefix = '',
  key = 'skeleton',
): unknown {
  for (const route of routes) {
    const full = [prefix, route.path ?? ''].filter(Boolean).join('/');

    if (pathMatchesRoute(full, path) && route.data?.[key] !== undefined) {
      return route.data[key];
    }

    if (route.children?.length) {
      const nested = findRouteData(
        route.children as Parameters<typeof findRouteData>[0],
        path,
        full,
        key,
      );
      if (nested !== undefined) {
        return nested;
      }
    }
  }

  return undefined;
}
